import { useState, useEffect } from 'react';
import { Bell, Mail, Save, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface EmailTemplate {
  name: string;
  description: string;
}

interface NotificationPreference {
  template_name: string;
  enabled: boolean;
}

export default function NotificationPreferences() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger tous les templates d'emails
      const { data: templatesData, error: templatesError } = await supabase
        .from('email_templates')
        .select('name, description')
        .order('name');

      if (templatesError) throw templatesError;

      // Charger les préférences de l'utilisateur
      const { data: preferencesData, error: preferencesError } = await supabase
        .from('user_notification_preferences')
        .select('template_name, enabled')
        .eq('user_id', user?.id);

      if (preferencesError) throw preferencesError;

      setTemplates(templatesData || []);
      
      // Créer un map des préférences existantes
      const preferencesMap = new Map(
        (preferencesData || []).map(p => [p.template_name, p.enabled])
      );

      // Créer la liste complète des préférences (par défaut activées)
      const allPreferences = (templatesData || []).map(template => ({
        template_name: template.name,
        enabled: preferencesMap.get(template.name) ?? true
      }));

      setPreferences(allPreferences);
    } catch (err: any) {
      console.error('Error loading notification preferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = (templateName: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.template_name === templateName 
          ? { ...pref, enabled: !pref.enabled }
          : pref
      )
    );
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Supprimer toutes les préférences existantes de l'utilisateur
      await supabase
        .from('user_notification_preferences')
        .delete()
        .eq('user_id', user.id);

      // Insérer les nouvelles préférences
      const preferencesToInsert = preferences.map(pref => ({
        user_id: user.id,
        template_name: pref.template_name,
        enabled: pref.enabled
      }));

      const { error: insertError } = await supabase
        .from('user_notification_preferences')
        .insert(preferencesToInsert);

      if (insertError) throw insertError;

      setSuccess('Préférences sauvegardées avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error saving notification preferences:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  const getTemplateIcon = (templateName: string) => {
    if (templateName.includes('new_transaction')) return '📝';
    if (templateName.includes('status_change')) return '🔄';
    if (templateName.includes('regularization_request')) return '⚠️';
    if (templateName.includes('regularization_approval')) return '✅';
    if (templateName.includes('objective_reminder')) return '🎯';
    return '📧';
  };

  const getTemplateLabel = (templateName: string) => {
    const labels: { [key: string]: string } = {
      'new_transaction_notification': 'Nouvelle transaction',
      'status_change_notification': 'Changement de statut',
      'regularization_request_notification': 'Demande de régularisation',
      'regularization_approval_notification': 'Validation de régularisation',
      'objective_reminder_notification': 'Rappel d\'objectifs'
    };
    return labels[templateName] || templateName;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Préférences de notification</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Sauvegarde...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Sauvegarder</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg flex items-center space-x-2">
          <XCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 text-green-600 rounded-lg flex items-center space-x-2">
          <CheckCircle2 className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-3 mb-6">
            <Mail className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Notifications par email</h3>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Choisissez les types de notifications que vous souhaitez recevoir par email. 
            Vous continuerez à recevoir les notifications internes dans l'application.
          </p>

          <div className="space-y-4">
            {preferences.map((preference) => {
              const template = templates.find(t => t.name === preference.template_name);
              return (
                <div
                  key={preference.template_name}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl">
                      {getTemplateIcon(preference.template_name)}
                    </span>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {getTemplateLabel(preference.template_name)}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {template?.description || 'Aucune description disponible'}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preference.enabled}
                      onChange={() => handleTogglePreference(preference.template_name)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              );
            })}
          </div>

          {preferences.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Aucun template de notification disponible
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-start space-x-3">
          <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">À propos des notifications</p>
            <p className="text-sm text-blue-700 mt-1">
              Ces paramètres contrôlent uniquement les notifications par email. 
              Les notifications internes dans l'application (cloche de notification) restent toujours actives 
              pour vous tenir informé des événements importants.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}